import { Box } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { RadioButton } from "#components/atoms/RadioButton/RadioButton";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";

type SelectableCardVariant = "compact" | "spacious";

type SelectableCardProps = {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  variant?: SelectableCardVariant;
};

type VariantStyle = {
  borderWidth: number;
  padding: string;
  gap: string;
  borderRadius: string;
  minWidth?: number;
  selectedBackgroundColor: string;
  descriptionSize: "base" | "small";
  alignItems: "flex-start" | "center";
  radioButtonSize: "standard" | "check";
};

const VARIANT_STYLES: Record<SelectableCardVariant, VariantStyle> = {
  compact: {
    borderWidth: 1,
    padding: spacing[4],
    gap: spacing[3],
    borderRadius: radius.sm,
    minWidth: 384,
    selectedBackgroundColor: colors.bg.elevation.tertiary,
    descriptionSize: "small",
    alignItems: "flex-start",
    radioButtonSize: "standard",
  },
  spacious: {
    borderWidth: 2,
    padding: spacing[6],
    gap: spacing[6],
    borderRadius: radius.xl,
    selectedBackgroundColor: colors.bg.elevation.primary,
    descriptionSize: "base",
    alignItems: "center",
    radioButtonSize: "check",
  },
};

export const SelectableCard = ({
  title,
  description,
  selected,
  onSelect,
  disabled = false,
  variant = "compact",
}: SelectableCardProps) => {
  const style = VARIANT_STYLES[variant];

  return (
    <Box
      onClick={disabled ? undefined : onSelect}
      style={{
        display: "flex",
        alignItems: style.alignItems,
        gap: style.gap,
        width: "100%",
        minWidth: style.minWidth,
        padding: style.padding,
        borderRadius: style.borderRadius,
        border: `${style.borderWidth}px solid ${
          selected ? colors.border.primaryDefault : colors.border.quaternary
        }`,
        backgroundColor: selected ? style.selectedBackgroundColor : colors.bg.elevation.primary,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box style={{ display: "flex", flexDirection: "column", gap: spacing[1], flex: 1 }}>
        <Title size="tiny">{title}</Title>
        <Text size={style.descriptionSize} color={colors.text.secondaryDefault} textAlign="left">
          {description}
        </Text>
      </Box>
      <RadioButton
        selected={selected}
        onChange={onSelect}
        disabled={disabled}
        size={style.radioButtonSize}
        aria-label={title}
      />
    </Box>
  );
};
